import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  VStack,
  useColorModeValue,
  Text,
  Button,
  HStack,
  Badge,
  Divider,
  FormControl,
  Textarea,
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { PrivateNavbar } from "../../components/layout/PrivateNavbar";
import { Footer } from "../../components/layout/Footer";
import { FiSend, FiArrowLeft } from "react-icons/fi";
import { ticketAPI } from "../../services/ticket-api";
import { useNavigate, useParams } from "react-router-dom";
import { Ticket, TicketPriority, TicketStatus } from "../../types";

export const TicketDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const messageBg = useColorModeValue("gray.50", "gray.700");
  const staffMessageBg = useColorModeValue("blue.50", "blue.900");

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadTicket();
    }
  }, [id]);

  const loadTicket = async () => {
    try {
      const data = await ticketAPI.getTicket(id!);
      setTicket(data);
    } catch (error) {
      console.error("Error loading ticket:", error);
      toast({
        title: t("support.detail.error.loading"),
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    setIsSubmitting(true);
    try {
      await ticketAPI.addMessage(id!, { content: newMessage });
      setNewMessage("");
      loadTicket();
      toast({
        title: t("support.detail.success.messageSent"),
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: t("support.detail.error.sending"),
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: TicketStatus): string => {
    const colors: Record<TicketStatus, string> = {
      [TicketStatus.OPEN]: "blue",
      [TicketStatus.IN_PROGRESS]: "orange",
      [TicketStatus.RESOLVED]: "green",
      [TicketStatus.CLOSED]: "gray",
    };
    return colors[status] || "gray";
  };

  const getPriorityColor = (priority: TicketPriority): string => {
    const colors: Record<TicketPriority, string> = {
      [TicketPriority.LOW]: "green",
      [TicketPriority.MEDIUM]: "blue",
      [TicketPriority.HIGH]: "orange",
      [TicketPriority.URGENT]: "red",
    };
    return colors[priority] || "gray";
  };

  if (!ticket) {
    return null;
  }

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            {/* Header */}
            <HStack spacing={4}>
              <IconButton
                aria-label="Back to tickets"
                icon={<FiArrowLeft />}
                onClick={() => navigate("/support")}
              />
              <VStack align="start" spacing={2} flex={1}>
                <Heading
                  size="xl"
                  fontFamily="Nasalization"
                  bgGradient="linear(to-r, brand.primary, brand.secondary)"
                  bgClip="text"
                >
                  {ticket.title}
                </Heading>
                <HStack spacing={4}>
                  <Badge colorScheme={getStatusColor(ticket.status)}>
                    {ticket.status}
                  </Badge>
                  <Badge colorScheme={getPriorityColor(ticket.priority)}>
                    {ticket.priority}
                  </Badge>
                  <Text color="gray.500">
                    {t("support.detail.created", {
                      date: new Date(ticket.createdAt).toLocaleDateString(
                        i18n.language === "pt-BR" ? "pt-BR" : "en-US"
                      ),
                    })}
                  </Text>
                </HStack>
              </VStack>
            </HStack>

            {/* Ticket Content */}
            <VStack spacing={6} bg={cardBg} p={8} borderRadius="xl" shadow="md">
              {/* Description */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("support.detail.description")}</Heading>
                <Text>{ticket.description}</Text>
              </VStack>

              <Divider />

              {/* Messages */}
              <VStack align="stretch" w="full" spacing={4}>
                <Heading size="md">{t("support.detail.conversation")}</Heading>
                <VStack align="stretch" spacing={4}>
                  {ticket.messages.map((message) => (
                    <Box
                      key={message.id}
                      bg={message.isStaffResponse ? staffMessageBg : messageBg}
                      p={4}
                      borderRadius="md"
                    >
                      <VStack align="stretch" spacing={2}>
                        <HStack justify="space-between">
                          <Text fontWeight="bold">
                            {message.isStaffResponse
                              ? t("support.detail.staff")
                              : message.user?.firstName ||
                                message.user?.email ||
                                t("support.detail.user")}
                          </Text>
                          <Text fontSize="sm" color="gray.500">
                            {new Date(message.createdAt).toLocaleString()}
                          </Text>
                        </HStack>
                        <Text>{message.content}</Text>
                      </VStack>
                    </Box>
                  ))}
                </VStack>
              </VStack>

              {/* Reply Form */}
              {ticket.status !== TicketStatus.CLOSED && (
                <VStack align="stretch" w="full" spacing={4}>
                  <Divider />
                  <FormControl>
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder={t("support.detail.replyPlaceholder")}
                      rows={4}
                    />
                  </FormControl>
                  <HStack justify="flex-end">
                    <Button
                      leftIcon={<FiSend />}
                      colorScheme="brand"
                      onClick={handleSendMessage}
                      isLoading={isSubmitting}
                      isDisabled={!newMessage.trim()}
                    >
                      {t("support.detail.send")}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
